import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useClaimAllResourcesMutation } from '@api/queries';
import { gameItemOrder, resourceList } from '@common/constants';
import { useDisabledDelay } from '@common/hooks/disabled-delay';
import { useTapRequestComplete } from '@common/hooks/tap-request-complete';
import { firebaseLogCollectResource } from '@common/lib/firebaseLog';
import { MainButton } from '@components/ui/Button/Button';
import { selectQuarriesAsObject } from '@redux/selectors/quarrySelectors';
import { useAppSelector } from '@redux/store';

import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { useCheckStorageOverflow } from '@/common/hooks/check-storage-overflow';
import { playSound } from '@/common/lib/soundEffect';

import CardsResources from './Card/CardsResources';

import stockPng from '/img/tabbar/office.png';
import stockWebp from '/img/tabbar/office.webp';

const TabsBody: React.FC = () => {
  const { t } = useTranslation();
  const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const [claimAllResources, { isLoading }] = useClaimAllResourcesMutation();
  const checkStockOverflow = useCheckStorageOverflow();
  const quarriesObject = useAppSelector(selectQuarriesAsObject);
  const [disabled, setDisabled] = useDisabledDelay();
  const storage = useAppSelector((state) => state.user.user?.storage);
  const previousValueRef = useRef(storage);
  const quarriesNextToBuild = useAppSelector(
    (state) => state.quarry.quarries?.nextToBuild,
  );
  const { tapRequestState, setTapRequestState } = useTapRequestComplete();

  const onClaim = async (): Promise<void> => {
    claimAllResources()
      .unwrap()
      .then(({ storage }) => {
        checkStockOverflow(storage, gameItemOrder.resource);
        ToastNotification(t('Resources sent to Stock'), {
          img: stockPng,
          webp: stockWebp,
        });
      })
      .catch((error) => {
        console.log(error);
        ToastNotification(
          t('Error: Failed to send resources'),
          undefined,
          'justify-center',
        );
      });
  };

  useEffect(() => {
    if (tapRequestState.isCompleted) {
      setDisabled(true);
      onClaim().then(() =>
        setTapRequestState((state) => ({ ...state, isCompleted: false })),
      );
    }
  }, [tapRequestState.isCompleted]);

  useEffect(() => {
    if (previousValueRef.current !== storage && quarriesObject) {
      firebaseLogCollectResource(
        quarriesObject,
        storage,
        previousValueRef.current,
      );
      previousValueRef.current = storage;
    }
  }, [storage]);

  return (
    <div className="tabs__body flex flex-col justify-between h-full">
      <div className="tabs__list">
        <CardsResources
          list={resourceList}
          nextToBuild={quarriesNextToBuild}
          buildsObject={quarriesObject}
          kind="resource"
        />
      </div>
      <MainButton
        isLoading={isLoading || tapRequestState.isLoading}
        disabled={isLoading || disabled}
        inactive={disabled || isFirstVisit}
        text={t('Send to Stock')}
        onClick={() => {
          playSound('buttons/switch');
          setTapRequestState({
            isLoading: true,
            isStarted: true,
            isCompleted: false,
          });
        }}
      />
    </div>
  );
};

export default TabsBody;
